import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { DtoTreatmentSalesgroup } from './models/DtoTreatmentSalesgroup';
import { DtoTreatmentSubSalesgroup } from './models/DtoTreatmentSubSalesgroup';
import { TreatmentSubSalesgroupComponent } from './treatment-subsalesgroup.component';
import { DialogService } from '@progress/kendo-angular-dialog';
import { PageSubheaderBarConfig } from '../../../fw/pages/page-config-api';
import { SettingsBaseService } from './settings-base.service';
import { FormService } from '../../../fw/services/form.service';
import { ValidationMessageComponent } from '../../shared/validation-message.component';
import { SharedValidators } from '../../shared/shared-validators';
import { SharedService } from '../../shared/shared.service';
import { Observable } from 'rxjs/Rx';
import { AutoUnsubscribe } from '../../../fw/decoractors/AutoUnsubscribe';
import {GridDataResult, SelectableSettings} from "@progress/kendo-angular-grid";
import {orderBy, SortDescriptor} from "@progress/kendo-data-query";
import {AsyncValidatorWrapper} from "../../../fw/helpers/async-validator-wrapper";

@Component({
    selector: 'treatment-salesgroup',
    template: require('./treatment-salesgroup.component.html'),
    styles: [require('./treatment-salesgroup.component.css')]
})
@AutoUnsubscribe()

export class TreatmentSalesgroupComponent implements OnInit {
    @Input() dialog: DialogRef;
    @Input() id: number;
    @Input() copyFromId: number;
    @ViewChild('validationMessage', {static: false}) validationMessage: ValidationMessageComponent;
    pageSubheaderBarConfig: PageSubheaderBarConfig;
    gridView: GridDataResult;
    form: FormGroup;
    dtoObject: DtoTreatmentSalesgroup;
    activeItems: Array<{ text: string, value: boolean }> = [
        { text: "Ja", value: true },
        { text: "Nee", value: false }
    ];
    selectedSubgroupIds: number[] = [];
    selectableSettings: SelectableSettings = {
        checkboxOnly: false,
        enabled: true,
        mode: "single"
    };

    constructor(private fb: FormBuilder,
                private fs: FormService,
                private ds: SettingsBaseService,
                private ss: SharedService,
                private dialogService: DialogService) { }

    ngOnInit() {
        this.pageSubheaderBarConfig = {
            title: 'SUBGROEPEN',
            buttonDefinition: [
                {
                    clickEventType: 'create',
                    description: 'TOEVOEGEN',
                    isSmall: true
                },
                {
                    clickEventType: 'copy',
                    description: 'KOPIËREN',
                    isSmall: true
                },
                {
                    clickEventType: 'edit',
                    description: 'WIJZIGEN',
                    isSmall: true
                },
                {
                    clickEventType: 'delete',
                    description: 'VERWIJDEREN',
                    isSmall: true
                }
            ]
        };

        this.form = this.fb.group({
            description: ['', [SharedValidators.customRequired]],
            index: [null,[SharedValidators.customRequired], [AsyncValidatorWrapper.debounce(this.validateIndex.bind(this))]],
            isActive: true
        });

        this.form.valueChanges.subscribe(() =>
            {if (this.validationMessage) this.validationMessage.getFormValidationErrors();}
        );

        if (this.id > 0) {
            this.ds.getTreatmentSalesgroupById(this.id)
                .subscribe(response => {
                    this.dtoObject = response;
                    this.fs.patchDtoToForm(this.dtoObject, this.form);
                    this.loadSubgroups();
                });
        }
        else {
            this.ds.getTreatmentSalesgroupNextIndex()
                .subscribe(res => {
                    this.dtoObject = {
                        id: 0,
                        isActive: true,
                        index: res,
                        treatmentSubSalesgroups: []
                    };
                    if (this.copyFromId > 0) {
                        this.ds.getTreatmentSalesgroupById(this.copyFromId)
                            .subscribe(response => {
                                this.dtoObject.description = response.description;
                                this.dtoObject.isActive = response.isActive;
                                this.fs.patchDtoToForm(this.dtoObject, this.form);
                                this.loadSubgroups();
                            });
                    }
                    else {
                        this.fs.patchDtoToForm(this.dtoObject, this.form);
                        this.loadSubgroups();
                    }
                });
        }
    }

    validateIndex(control: AbstractControl) {
        return Observable.create(observer => {
            if (this.dtoObject != null && control.dirty && control.value != null) {
                this.ds.checkTreatmentSalesgroupIndexAvailable(this.dtoObject.id, control.value)
                    .subscribe((response: boolean) => {
                        observer.next(response ? null : { indexNotAvailable: true });
                        observer.complete();
                        if (this.validationMessage) this.validationMessage.getFormValidationErrors();
                    });
            }
            else {
                observer.next(null);
                observer.complete();
                if (this.validationMessage) this.validationMessage.getFormValidationErrors();
            }
        });
    }

    btnCancelClicked(event) {
        this.closeWindow(false);
    }

    btnSaveClicked(event) {
        this.validationMessage.validateForm();

        if (this.form.valid) {
            let result = this.fs.patchFormToDto(this.dtoObject, this.form);
            this.ds.saveTreatmentSalesgroup(result)
                .subscribe(response => {
                    if (!response.hasError) {
                        this.closeWindow(true);
                    }
                    else {
                        this.ss.message(response.errorMessage);
                    }
                });
        }
    }

    btnSubHeaderBarClicked(event) {
        if (event == 'create') {
            this.showTreatmentSubSalesgroupsEditWindow(null, null);
        } else if (event == 'edit') {
            if (this.getSelectedSubgroup()) {
                this.showTreatmentSubSalesgroupsEditWindow(this.getSelectedSubgroup(), null);
            }
        } else if (event == 'copy') {
            if (this.getSelectedSubgroup()) {
                this.showTreatmentSubSalesgroupsEditWindow(null, this.getSelectedSubgroup());
            }
        } else if (event == 'delete') {
            if (this.getSelectedSubgroup()) {
                this.ss.confirm('Weet u zeker dat u dit record wilt verwijderen?')
                    .subscribe((res: boolean) => {
                        if (res) {
                            this.dtoObject.treatmentSubSalesgroups.splice(this.dtoObject.treatmentSubSalesgroups.indexOf(this.getSelectedSubgroup()), 1);
                            this.loadSubgroups();
                        }
                    });
            }
        }
    }

    showTreatmentSubSalesgroupsEditWindow(subGroupObject: DtoTreatmentSubSalesgroup, copyFromObject: DtoTreatmentSubSalesgroup) {
        const dialogRef = this.dialogService.open({
            content: TreatmentSubSalesgroupComponent
        });

        let isNewObject = false;
        if (!subGroupObject) {
            subGroupObject = {
                id: 0,
                isActive: true,
                index: 0
            };
            isNewObject = true;
        }

        const dialogContent: TreatmentSubSalesgroupComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.parentId = this.dtoObject.id;
        dialogContent.dtoObject = subGroupObject;
        dialogContent.copyFromObject = copyFromObject;
        dialogContent.currentObjects = this.dtoObject.treatmentSubSalesgroups;
        dialogContent.isNewObject = isNewObject;
        dialogContent.subgroupSaved
            .subscribe((result: DtoTreatmentSubSalesgroup) => {
                if (isNewObject) {
                    this.dtoObject.treatmentSubSalesgroups.push(result);
                } else {
                    let currentIndex = this.dtoObject.treatmentSubSalesgroups.indexOf(subGroupObject);
                    if (this.dtoObject.treatmentSubSalesgroups[currentIndex]) {
                        this.dtoObject.treatmentSubSalesgroups[currentIndex] = result;
                    }
                }
                this.loadSubgroups();
            });
    }

    loadSubgroups() {
        let sort:  SortDescriptor[] = [{
            dir: "asc",
            field: "index"
        }];
        this.gridView = {
            data: orderBy(this.dtoObject.treatmentSubSalesgroups, sort),
            total: this.dtoObject.treatmentSubSalesgroups.length
        };
        this.selectedSubgroupIds = [];
    }

    getSelectedSubgroup(): DtoTreatmentSubSalesgroup {
        if (this.selectedSubgroupIds.length == 1) {
            return this.dtoObject.treatmentSubSalesgroups[this.dtoObject.treatmentSubSalesgroups.findIndex(x => x.id == this.selectedSubgroupIds[0])];
        }
        return null;
    }

    rowdoubleClicked() {
        if (this.getSelectedSubgroup()) {
            this.showTreatmentSubSalesgroupsEditWindow(this.getSelectedSubgroup(), null);
        }
    }

    closeWindow(result: boolean) {
        if (this.dialog) {
            this.dialog.dialog.instance.action.emit(result);
            this.dialog.close();
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-base/treatment-salesgroup.component.ts